/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useRef, useEffect, useState, useMemo } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
// import { Chart as ChartJS, registerables } from 'chart.js';
// import { Chart } from 'react-chartjs-2'
// ChartJS.register(...registerables);
// import 'chartjs-adapter-moment';

// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftSelect from "components/SoftComponents/SoftSelect";

import DashboardLayout from "template/examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "template/examples/Navbars/DashboardNavbar";
import Footer from "template/examples/Footer";

// Soft UI Dashboard PRO React helper functions
import gradientChartLine from "assets/theme/functions/gradientChartLine";

// Soft UI Dashboard PRO React base styles
import colors from "assets/theme/base/colors";

import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';

//Backend access
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { getLogs, loadToken } from 'services/api/bks';

import { Route, Link, Routes, useLocation, useNavigate, useMatch } from 'react-router-dom';
import moment from "moment";

import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import ColumnChart from "../ColumnChart";
import reportsAPI from 'services/api/reports';

async function processChartData(data, uuid) {
    // 1773151200

    var colours = [
        '#2d385a',
        '#67727a',
        '#1a348a',
        '#2f323b'
    ]


    var labels = [];
    var datasets = {};
    var weekly_data = [];
    var seven_day_temp = [];
    var seven_day_data = {};

    var totals = {};

    for (var uuid in data) {
        var device = data[uuid];

        for (var key in device) {
            labels.push(key * 1000);

            for (var zone in device[key]['infringement_zones']) {

                // Normal Data
                if (!(datasets?.[zone])) {
                    datasets[zone] = {
                        label: zone,
                        data: [],
                        order: 2,
                        type: 'bar',
                        yAxisID: 'y'
                    };
                }

                if (typeof totals[zone] == 'undefined') {
                    totals[zone] = 0;
                }
                totals[zone] += device[key]['infringement_zones'][zone];

                datasets[zone].data.push({
                    'x': key * 1000,
                    'y': device[key]['infringement_zones'][zone],
                    'data': device[key]['infringement_zones'][zone]
                });
            }

            // Weekly Average
            if (!weekly_data.length || device[key].day_of_week == 0) {
                weekly_data.push({
                    'x': key * 1000,
                    'y': device[key]['total_infringement_count'],
                    'count': 0
                });
            }
            weekly_data[(weekly_data.length - 1)]['count']++;
            weekly_data[(weekly_data.length - 1)]['y'] += device[key]['total_infringement_count'];

            // Rolling 7 Day Avg
            seven_day_temp.push(device[key]['total_infringement_count']);
            if (seven_day_temp.length > 7) {
                seven_day_temp.splice(0, 1);
            }

            // console.log(seven_day_temp)

            if (seven_day_temp.length == 7) {
                if (!seven_day_data[key]) {
                    seven_day_data[key] = {
                        'x': key * 1000,
                        'y': seven_day_temp.reduce((a, b) => a + b, 0) / seven_day_temp.length
                    }
                }
            }
        }
    }

    // Normal Data
    var chart_data = {
        labels,
        datasets: Object.values(datasets).map((row, index) => {
            row.backgroundColor = colours[index]
            return row;
        })
    }

    // Weekly Average
    chart_data.datasets.push({
        label: 'Weekly Average',
        data: weekly_data.map((row) => {
            row.y = row.y / row.count
            return row;
        }).filter(row => row.y != 0),
        backgroundColor: '#a50570',
        borderColor: '#a50570',
        type: 'line',
        order: 1,
        yAxisID: 'y1'
    });

    // console.log(Object.values(seven_day_data))

    // Rolling 7 Days Average
    chart_data.datasets.push({
        label: 'Rolling 7-Day Avg',
        data: Object.values(seven_day_data),
        backgroundColor: '#acdb00',
        borderColor: '#acdb00',
        type: 'line',
        order: 1,
        yAxisID: 'y1'
    });

    // console.log(totals)
    return chart_data;
}

function ReportingColumnChart({ device, title, startTime, setStartTime, endTime, setEndTime, groupSize, setGroupSize, height }) {
    // console.log(groupSize)
    loadToken(useAuth0());

    const chartRef = useRef(null);
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);

            var data = await reportsAPI.getCameraObjectsData([device.uuid], startTime.unix(), endTime.unix(), { devices: [device] });

            var chart_data = await processChartData(data, device.uuid);

            setChartData(chart_data);
            setLoading(false);
        };
        fetchData();
    }, [startTime, endTime, device]);

    return (
        <Card>
            <SoftBox p={{ xxs: 1, xs: 2 }}>
                <SoftBox sx={{ opacity: loading ? 0.3 : 1 }}>
                    <SoftBox m={1} display="flex" justifyContent="center" alignItems="center">
                        <SoftTypography variant="h6">
                            {title}
                        </SoftTypography>
                    </SoftBox>
                    <SoftBox sx={{ minHeight: '200px', height: height, width: 'auto' }}>
                        {chartData && <ColumnChart chartData={chartData} startTime={startTime.unix() * 1000} endTime={endTime.unix() * 1000} groupSize={groupSize} setStartTime={setStartTime} setEndTime={setEndTime} setGroupSize={setGroupSize} />}
                    </SoftBox>
                </SoftBox>
            </SoftBox>
        </Card>
    );

}

// Typechecking props for the ReportingColumnChart
ReportingColumnChart.propTypes = {
    device: PropTypes.object,
    title: PropTypes.string,
    startTime: PropTypes.instanceOf(moment()),
    setStartTime: PropTypes.func,
    endTime: PropTypes.instanceOf(moment()),
    setEndTime: PropTypes.func,
    groupSize: PropTypes.number,
    setGroupSize: PropTypes.func,
    height: PropTypes.number
};

export default ReportingColumnChart;

